import { useEffect, useState, useCallback } from "react";
import { CloudOff, RefreshCw, AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/App";
import { toast } from "sonner";
import offlineStorage from "@/utils/offlineStorage";
import { manualFlush } from "@/utils/pendingOrderSync";

/**
 * Compact badge showing the count of orders queued in IndexedDB awaiting sync.
 * Click → popover with the queue details (source, attempts, last error) and a
 * "Riprova ora" button that forces a manual flush. Hidden when count is 0.
 */
export default function PendingOrdersBadge({ className = "" }) {
  const { authAxios } = useAuth();
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [retrying, setRetrying] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const list = await offlineStorage.getPendingOrders();
      setItems(list);
      setCount(list.length);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    refresh();
    window.addEventListener("pendingOrdersChanged", refresh);
    window.addEventListener("pendingOrdersFlushed", refresh);
    return () => {
      window.removeEventListener("pendingOrdersChanged", refresh);
      window.removeEventListener("pendingOrdersFlushed", refresh);
    };
  }, [refresh]);

  const handleRetry = async () => {
    if (retrying) return;
    setRetrying(true);
    try {
      const res = await manualFlush(authAxios);
      if (!res.ok) {
        toast.error("Sei ancora offline — riprova quando torna la rete");
      } else if (res.sent > 0 && (res.remaining || 0) === 0) {
        toast.success(`Sincronizzati ${res.sent} ordini`);
      } else if (res.sent > 0) {
        toast.success(`Sincronizzati ${res.sent}, restano ${res.remaining} in coda`);
      } else if (res.failed > 0) {
        toast.error(`Sync fallito su ${res.failed} ordini — vedi dettagli`);
      } else {
        toast.message("Nessun ordine da sincronizzare");
      }
      await refresh();
    } finally {
      setRetrying(false);
    }
  };

  if (count <= 0) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="pending-orders-badge-trigger"
          className="focus:outline-none focus:ring-2 focus:ring-amber-500/50 rounded"
          title="Tocca per vedere e riprovare la sincronizzazione"
        >
          <Badge
            data-testid="pending-orders-badge"
            variant="outline"
            className={`flex items-center gap-1 bg-amber-500/15 text-amber-300 border-amber-500/30 hover:bg-amber-500/25 cursor-pointer ${className}`}
          >
            <CloudOff className="w-3 h-3" />
            <span className="text-[10px] font-bold">{count} in coda</span>
          </Badge>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 bg-[#18181b] border-white/10 text-zinc-200 p-3"
        data-testid="pending-orders-popover"
      >
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold text-amber-300">Ordini in attesa di sync</p>
          <button
            type="button"
            onClick={handleRetry}
            disabled={retrying}
            data-testid="pending-orders-retry-btn"
            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 ${retrying ? "animate-spin" : ""}`} />
            Riprova ora
          </button>
        </div>
        <p className="text-[11px] text-zinc-500 mb-2">
          Verranno inviati automaticamente quando torna la rete (riprova ogni minuto).
        </p>
        <div className="max-h-64 overflow-y-auto space-y-1.5">
          {items.map((it) => {
            const created = it.created_at ? new Date(it.created_at).toLocaleString("it-IT") : "";
            const linesCount = it.payload?.items?.length || 0;
            const clientName = it.payload?.client_name || "Ordine";
            return (
              <div
                key={it.id}
                data-testid={`pending-order-item-${it.id}`}
                className="text-[11px] p-2 rounded bg-black/30 border border-white/5"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-zinc-200 truncate">{clientName}</span>
                  <span className="text-[10px] text-zinc-500 shrink-0">{linesCount} ART.</span>
                </div>
                <div className="text-[10px] text-zinc-500 mt-0.5">
                  {it.source} · {created}
                  {it.attempts > 0 && <> · tentativi: {it.attempts}</>}
                </div>
                {it.last_error && (
                  <div className="text-[10px] text-red-400 mt-0.5 flex items-start gap-1">
                    <AlertTriangle className="w-3 h-3 shrink-0 mt-px" />
                    <span className="break-words">{it.last_error}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
